import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    broadcast,
    GroupChatAvatarImg,
    ImgUserPlaceholder,
    loaderSVG,
    SampleChatProfile,
    SampleGroupProfile,
    SingleChatAvatarImg2,
} from '../../../assets/images';
import { classNames, isSingleChat } from '../../../Helpers/Chat/ChatHelper';
import IndexedDb from '../../../Helpers/IndexedDb';
import { getColorCodeInitials, getInitialsFromName, isPhoneNumber } from '../../../Helpers/Utility';
import SvgProfileName from './SvgProfileName';

const avatarIconObject = {
    chat: SampleChatProfile,
    groupchat: SampleGroupProfile,
    broadcast: broadcast,
    loginProfile: ImgUserPlaceholder,
};

const CHAT_TYPES = {
    GROUP_CHAT: 'groupchat',
};

const computeInitialsData = (chatType, name, blocked) => {
    if (isSingleChat(chatType) && name && !blocked && !isPhoneNumber(name)) {
        const initialName = getInitialsFromName(name);
        if (initialName) {
            return {
                show: true,
                colorCode: getColorCodeInitials(name),
                inital: initialName,
            };
        }
    }
    return { show: false, colorCode: '', inital: '' };
};

const ImageComponent = React.memo(
    ({
        userToken,
        imageToken,
        fileKey = '',
        blocked = false,
        chatType,
        temporary,
        getImageUrl,
        onclickHandler,
        imageType = 'profileimages',
        name = '',
        hideClassNameStatus = false,
        ...props
    }) => {
        const localDb = useMemo(() => new IndexedDb(), []);
        const avatarIcon = useMemo(
            () => (chatType ? avatarIconObject[chatType] : avatarIconObject['chat']),
            [chatType],
        );
        const [getImage, setGetImage] = useState(null);
        const [imgPointer, setImgPointer] = useState(false);
        const [imageLoadFailed, setImageLoadFailed] = useState(false);
        const initialsData = useMemo(() => computeInitialsData(chatType, name, blocked), [chatType, name, blocked]);
        // Prefer a name initial over the generic avatar when there is no token, or when the
        // token's image could not be loaded (e.g. a call log peer whose profile image blob
        // is not available on this device). Falls through to the default only when we also
        // have no usable name.
        const showInitials = initialsData.show && (!imageToken || imageLoadFailed);

        const isSubscribedRef = useRef(true);

        useEffect(() => {
            isSubscribedRef.current = true;
            let createdBlobUrl = null;
            // Reset the failure flag for a fresh token so a new valid image is not hidden.
            setImageLoadFailed(false);

            if (blocked || !imageToken) {
                setGetImage(null);
                return () => {
                    isSubscribedRef.current = false;
                };
            }

            if (/^(blob:http|blob:https):\/\//i.test(imageToken)) {
                setGetImage(imageToken);
                return () => {
                    isSubscribedRef.current = false;
                };
            }

            localDb
                .getImageByKey(imageToken, imageType, fileKey)
                .then(blob => {
                    if (isSubscribedRef.current) {
                        const blobUrl = window.URL.createObjectURL(blob);
                        createdBlobUrl = blobUrl;
                        setGetImage(blobUrl);
                    }
                })
                .catch(() => {
                    if (isSubscribedRef.current) {
                        setGetImage(null);
                        setImgPointer(true);
                        // The token's image could not be fetched — let the render fall back
                        // to the name initial instead of the generic avatar.
                        setImageLoadFailed(true);
                    }
                });

            return () => {
                isSubscribedRef.current = false;
                if (createdBlobUrl) window.URL.revokeObjectURL(createdBlobUrl);
            };
        }, [imageToken, blocked, localDb, imageType, fileKey]);

        const onImageLoad = useCallback(event => {
            event.target.style.backgroundImage = '';
        }, []);

        const checkNotFound = useCallback(
            event => {
                const initials = computeInitialsData(chatType, name, blocked);
                // A name initial reads better than the generic avatar when the image errors.
                if (initials.show) {
                    setImageLoadFailed(true);
                } else {
                    event.target.src = avatarIcon;
                }
            },
            [chatType, name, blocked, avatarIcon],
        );

        const addclass = useMemo(() => (props.classProps ? classNames(props.classProps) : ''), [props.classProps]);

        const updatedUrl = useMemo(() => getImage || avatarIcon, [getImage, avatarIcon]);

        useEffect(() => {
            if (getImageUrl) {
                getImageUrl(updatedUrl);
            }
        }, [updatedUrl, getImageUrl]);

        const handleKeyDown = useCallback(
            e => {
                if ((e.key === 'Enter' || e.key === ' ') && updatedUrl !== avatarIcon) {
                    onclickHandler?.(e);
                }
            },
            [updatedUrl, avatarIcon, onclickHandler],
        );

        const handleClick = useMemo(
            () => (updatedUrl !== avatarIcon ? onclickHandler : null),
            [updatedUrl, avatarIcon, onclickHandler],
        );

        const imageStyle = useMemo(
            () => ({
                backgroundImage: `url(${loaderSVG})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: 'contain',
                // minWidth: hideClassNameStatus ? '' : '50px',
                // minHeight: hideClassNameStatus ? '' : '50px',
            }),
            [hideClassNameStatus],
        );

        const imageClassName = useMemo(
            () => `${addclass} ${props.caption === '' ? 'no-caption' : ''} ${imgPointer ? 'cursor-default' : ''}`,
            [addclass, props.caption, imgPointer],
        );

        if (imageToken && !imageLoadFailed) {
            return (
                <img
                    src={updatedUrl}
                    data-jest-id={'jestImageComponent'}
                    style={imageStyle}
                    onLoad={onImageLoad}
                    className={imageClassName}
                    onError={checkNotFound}
                    alt=""
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    role="button"
                    tabIndex={updatedUrl !== avatarIcon ? 0 : -1}
                />
            );
        }

        if (!imageToken && chatType === CHAT_TYPES.GROUP_CHAT) {
            return <GroupChatAvatarImg />;
        }

        if (showInitials) {
            return (
                <SvgProfileName
                    userShortName={initialsData.inital}
                    initialColor={initialsData.colorCode}
                    hideClassNameStatus={hideClassNameStatus}
                />
            );
        }

        return <SingleChatAvatarImg2 />;
    },
);

ImageComponent.displayName = 'ImageComponent';

export default ImageComponent;

ImageComponent.propTypes = {
    imgSrc: PropTypes.string,
    handleMediaShow: PropTypes.func,
    isSender: PropTypes.bool,
    uploadStatus: PropTypes.number,
    messageObject: PropTypes.object,
    imgFileDownloadOnclick: PropTypes.func,
    imageHeightAdjust: PropTypes.bool,
    pageType: PropTypes.string,
    imageUrlDownloaded: PropTypes.string,
    userToken: PropTypes.string,
    imageToken: PropTypes.string,
    fileKey: PropTypes.string,
    blocked: PropTypes.bool,
    chatType: PropTypes.oneOf(['chat', 'groupchat', 'broadcast', 'loginProfile']),
    temporary: PropTypes.bool,
    getImageUrl: PropTypes.func,
    onclickHandler: PropTypes.func,
    imageType: PropTypes.string,
    name: PropTypes.string,
    classProps: PropTypes.object,
    caption: PropTypes.string,
};
